// Price check writes + reads. Kept out of the components so the card, the panel
// and the Overview list all save a price the same way.
import axios from "axios";
import ListingHelper from "../../helpers/Listing";
import { requireResponse } from "../../core/errors/requireResponse";
import { parseSkipCompCheck } from "./priceCheck";

const SILENT = { metadata: { silent: true } };

/**
 * Save ONE channel price ({mp}_price, major units in → pence on the wire).
 *
 * Goes through the very request the listing edit form's auto-save sends —
 * PUT /api/listings/{id} with the form's own initial-values → submit-values
 * pipeline over a fresh GET — with only `{mp}_price` changed. There is no
 * narrow authorised per-channel price endpoint (PUT …/chrono-ebay is the
 * external checker's unauthenticated-by-policy write path), and a partial
 * PUT /api/listings/{id} 422s on the required product block. The backend's
 * ListingObserver recomputes {mp}_is_comp on this save.
 *
 * Resolves to the saved listing as the API returns it.
 */
export const saveChannelPrice = async (listingId, mp, major) => {
  const { data: fresh } = requireResponse(await axios.get(`/api/listings/${listingId}`));
  const values = ListingHelper.getInitialValues(fresh);
  const { marketplace_states: _states, marketplace_images, _touched_scopes, ...listingValues } = values;
  const payload = {
    ...ListingHelper.processSubmitValues({ ...listingValues, [`${mp}_price`]: Number(major) }),
    marketplace_images: ListingHelper.pickTouchedMarketplaceImages(marketplace_images, _touched_scopes),
  };
  const { data: saved } = requireResponse(
    await axios.put(`/api/listings/${listingId}`, payload, SILENT)
  );
  return saved && typeof saved === "object" ? { ...fresh, ...saved } : fresh;
};

/**
 * Save the price of a non-legacy Chrono24 account (CH24 Europe…), major units
 * of the account's currency in → minor units on the wire, through the same
 * per-account endpoint and payload builder the listing form uses. Resolves to
 * the saved junction row.
 */
export const saveAccountPrice = async (listingId, accountId, major) => {
  const body = ListingHelper.buildMarketplaceStatePayload({ price: Number(major) });
  const { data } = requireResponse(
    await axios.put(`/api/listings/${listingId}/marketplaces/${accountId}`, body, SILENT)
  );
  return data;
};

/**
 * Mark a marketplace reviewed (hides it for 14 days) or undo that. Every
 * Chrono24 account shares the "chrono" flag — they are judged from one check.
 * The endpoint takes the WHOLE skip map, so the other marketplaces' current
 * flags are sent back unchanged.
 */
export const setReviewed = async (listing, mp, on = true) => {
  const next = { ebay: false, chrono: false, wf: false, dao: false, ...parseSkipCompCheck(listing), [mp]: !!on };
  const { data } = requireResponse(
    await axios.put(`/api/listings/${listing.id}/skip-comp-check`, { skip_comp_check: next }, SILENT)
  );
  return data && typeof data === "object" ? { ...listing, ...data } : { ...listing, skip_comp_check: next };
};

export const fetchPriceCheckSummary = async () => {
  const { data } = requireResponse(
    await axios.get("/api/listings/competitive-analysis/summary", SILENT)
  );
  return data;
};

export const fetchFlaggedListings = async ({ offset = 0, limit = 50 } = {}) => {
  const { data } = requireResponse(
    await axios.post("/api/listings/competitive-analysis/missing/all", { paging: { offset, limit } }, SILENT)
  );
  return { items: data?.items ?? [], total: data?.total ?? 0, hasMore: !!data?.hasMore };
};
